import api from "../../../utils/api";
import { unwrap, unwrapList } from "./apiEnvelope";

// ============================================================================
// CRM Integrations (Integraciones) — frontend service. Talks to the
// torre-de-control-api crmIntegrations module. Stores the client's API keys for
// the external providers the CRM connects to (ElevenLabs, Twilio, Anthropic,
// Google Calendar…), one record per provider.
//
//   • list   (authed): GET    /crm/integrations
//   • get    (authed): GET    /crm/integration?id=
//   • update (authed): PUT    /crm/integrations?id=   body { values: {key: val} }
//   • clear  (authed): DELETE /crm/integrations?id=
//   • perms  (authed): GET    /crm/integrations/permissions  (#671)
//
// #671: reading needs the `view` permission; saving / clearing the global keys
// needs `manage_global` (platform admin / «admin reseller» only; a client
// company's plain `admin` gets 403). A denial answers 403 with
// meta.code = "PERMISSION_DENIED", which utils/api.ts does NOT treat as an
// expired session.
//
// Llamadas de IA, softphone, SMS y correo ya leen lo guardado aquí: lo capturado
// manda sobre el `.env`, que queda como respaldo. Cada compañía elige por servicio
// si usa las llaves de la plataforma o las suyas (`source`); un administrador de
// plataforma captura las compartidas con la vista `platform` (`?scope=global`).
// ============================================================================

export type FieldType = "secret" | "text";
export type IntegrationStatus = "stored" | "inherited" | "env" | "unconfigured";
/** De quién son las llaves que usa la compañía; `null` = no ha elegido. */
export type IntegrationSource = "platform" | "own" | null;
/** Qué bóveda se ve: la de la compañía o la compartida por toda la plataforma. */
export type IntegrationVault = "company" | "platform";

const vaultParams = (vault: IntegrationVault) => (vault === "platform" ? { scope: "global" } : {});

/** One credential field within a provider, as returned by the API (masked). */
export interface IntegrationField {
  key: string;
  label: string;
  type: FieldType;
  placeholder: string;
  envVar: string;
  hasStored: boolean; // a value is saved in THIS vault (the company's own)
  /** Only set in the shared vault, which the company inherits (value not returned). */
  inherited?: boolean;
  hasEnv: boolean; // the matching .env var is set on the server
  value: string | null; // text fields only (secrets are never returned in full)
  preview: string | null; // masked preview for secret fields (e.g. ••••1234)
}

export interface Integration {
  id: string; // provider id (elevenlabs | twilio | anthropic | google_calendar)
  name: string;
  description: string;
  icon: string; // remixicon class
  docsUrl: string;
  status: IntegrationStatus;
  /** "global" → configured once per deployment (HubSpot): platform admins only. */
  scope?: "company" | "global";
  source?: IntegrationSource;
  /** La compañía puede elegir (no aplica a la bóveda compartida ni a HubSpot). */
  canChooseSource?: boolean;
  fields: IntegrationField[];
}

export async function fetchIntegrations(vault: IntegrationVault = "company"): Promise<Integration[]> {
  const res = await api.get("/crm/integrations", { params: vaultParams(vault) });
  return unwrapList<Integration>(res);
}

/** Elige de quién son las llaves de un servicio para la compañía; no borra nada. */
export async function setIntegrationSource(id: string, source: IntegrationSource): Promise<Integration> {
  const res = await api.put("/crm/integrations/source", { source }, { params: { id } });
  return unwrap<Integration>(res);
}

/**
 * Upsert a provider's keys. Only the fields present in `values` are written; a
 * field sent as "" is cleared; omitted fields are left untouched. Secret fields
 * left blank in the UI should simply be omitted (don't overwrite with "").
 */
export async function updateIntegration(
  id: string,
  values: Record<string, string>,
  vault: IntegrationVault = "company",
): Promise<Integration> {
  const res = await api.put("/crm/integrations", { values }, { params: { id, ...vaultParams(vault) } });
  return unwrap<Integration>(res);
}

/** Clear all stored keys for a provider (falls back to .env on the server). */
export async function clearIntegration(id: string, vault: IntegrationVault = "company"): Promise<Integration> {
  const res = await api.delete("/crm/integrations", { params: { id, ...vaultParams(vault) } });
  return unwrap<Integration>(res);
}

// --- Catálogo único (#1130) ------------------------------------------------
// GET /integrations?category= — TODO lo que la plataforma conecta (terceros y
// productos de la familia) con su estado; lo pinta <IntegrationsPanel>. Las
// llaves se siguen editando con updateIntegration/clearIntegration.

export type CatalogKind = "credentials" | "link" | "env" | "product";
export type CatalogStatus =
  | IntegrationStatus
  | "link"
  | "partial"
  | "connected"
  | "unreachable"
  | "pending"
  | "configured";

export interface CatalogCategory {
  id: string;
  name: string;
}

export interface CatalogItem {
  id: string;
  name: string;
  description: string;
  icon: string;
  categories: string[];
  kind: CatalogKind;
  route: string | null;
  status: CatalogStatus;
  /** Sólo `credentials`: la misma vista enmascarada de /crm/integrations. */
  provider?: Integration;
  /** Sólo `env`: qué variables existen en el servidor (nunca su valor). */
  envVars?: Array<{ name: string; present: boolean }>;
  /** Sólo `product`: compañías alcanzadas o el motivo del fallo. */
  detail?: string;
}

export interface IntegrationCatalog {
  categories: CatalogCategory[];
  integrations: CatalogItem[];
  canManage: boolean;
}

export async function fetchIntegrationCatalog(category?: string): Promise<IntegrationCatalog> {
  const res = await api.get("/integrations", { params: category ? { category } : {} });
  // El controlador responde `{ message, data: {…} }` dentro del sobre: leerlo un
  // nivel arriba dejaba la lista vacía y la pantalla en sólo lectura con un 200.
  const data = unwrap<Partial<IntegrationCatalog>>(res) ?? {};
  return {
    categories: Array.isArray(data.categories) ? data.categories : [],
    integrations: Array.isArray(data.integrations) ? data.integrations : [],
    canManage: Boolean(data.canManage),
  };
}

// --- Permissions (#671) -----------------------------------------------------

export type IntegrationPermission = "view" | "connect" | "manage_global";

export interface IntegrationPermissions {
  loaded: boolean;
  /** Company admin or platform admin. Does NOT imply manage_global. */
  isAdmin: boolean;
  /** Company the requests act on (from the signed session, never the header). */
  companyId: string | null;
  permissions: Record<IntegrationPermission, boolean>;
}

export const NO_INTEGRATION_PERMISSIONS: Record<IntegrationPermission, boolean> = {
  view: false,
  connect: false,
  manage_global: false,
};

/**
 * #671 — may the caller write/wipe the deployment-wide integration keys?
 * ONLY the explicit `manage_global` flag counts (the backend grants it to the
 * platform admin, «admin reseller», alone). `isAdmin` is deliberately ignored:
 * a client company's plain admin is isAdmin and still gets 403 on PUT/DELETE.
 */
export function canManageGlobalIntegrations(perms: IntegrationPermissions): boolean {
  return perms.permissions.manage_global === true;
}

/**
 * WAYAK keeps the keys PER COMPANY: the company's own admin may write them
 * (the API's requireIntegrationKeysWriter), a platform admin may write any.
 * Plain CRUD users only read the masked status.
 */
export function canManageCompanyIntegrations(perms: IntegrationPermissions): boolean {
  return canManageGlobalIntegrations(perms) || (perms.isAdmin === true && Boolean(perms.companyId));
}

/**
 * The caller's own effective integration permissions. Fails closed: if the
 * request fails, every write action is hidden.
 */
export async function fetchIntegrationPermissions(): Promise<IntegrationPermissions> {
  try {
    const res = await api.get("/crm/integrations/permissions");
    const data = res.data?.data ?? {};
    return {
      loaded: true,
      isAdmin: Boolean(data.isAdmin),
      companyId: data.companyId ?? null,
      permissions: { ...NO_INTEGRATION_PERMISSIONS, ...(data.permissions ?? {}) },
    };
  } catch {
    return { loaded: true, isAdmin: false, companyId: null, permissions: NO_INTEGRATION_PERMISSIONS };
  }
}
